import { useAppSelector } from '../../useReduxToolkit';
import {
  getScreenshareStartedTime,
  getScreenshareState,
} from '../../../features/screenshare/screenshare.selectors';
import { useEffect } from 'react';
import { type TransitionFunc } from '../screenshareState.types';
import {
  ScreenshareState,
  ScreenshareTransition,
} from '../../../features/screenshare/screenshare.types';
import { useCallParticipants } from '../../useCallParticipants';
import { useLocalMedia } from '../../useLocalMedia';
import { RealtimeRole } from '@doxyme/realtime';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { TrackedEvents } from '../../../features/tracking/track';
import { useTrack } from '../../useTrack';

export const useScreenshareInProgress = (
  onTransition: TransitionFunc,
): (() => Promise<void>) => {
  const track = useTrack();
  const screenshareState = useAppSelector(getScreenshareState);
  const startedAt = useAppSelector(getScreenshareStartedTime);
  const { screenSharingParticipant } = useCallParticipants();
  const { screenElement } = useLocalMedia();
  const { role } = useAppSelector(getApolloSessionDetails);

  useEffect(() => {
    const startedMoreThan1SecondAgo =
      startedAt !== undefined && Date.now() - startedAt > 1000;

    if (
      screenshareState === ScreenshareState.InProgress &&
      !screenSharingParticipant &&
      !screenElement &&
      startedMoreThan1SecondAgo
    ) {
      void onTransition(ScreenshareTransition.STOP_SCREENSHARE);
    }
  }, [screenshareState, screenSharingParticipant, screenElement]);

  return async (): Promise<void> => {
    if (role === RealtimeRole.Provider) {
      void track(TrackedEvents.ScreenshareStarted, {
        sharedBy: screenSharingParticipant?.identity?.role ?? role,
      });
    }

    // nothing to do here, we just need the useEffect to run
    return undefined;
  };
};
